// hooks/useStake.js
/* eslint-env es2020 */
import { useEffect, useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { useAccount } from 'wagmi';
import { readContract } from 'wagmi/actions';
import { formatUnits } from 'viem';
import { newPool } from '../utils/contract';

function safeFormatUnits(value, decimals = 18) {
    try {
        if (value == null) return '0';

        let bigVal;
        if (typeof value === 'bigint') {
            bigVal = value;
        } else {
            let strVal = String(value).trim();
            if (strVal.endsWith('n')) strVal = strVal.slice(0, -1);
            strVal = strVal.replace(/[^\d]/g, '');
            if (strVal === '' || strVal === '0') return '0';
            bigVal = BigInt(strVal);
        }

        if (decimals < 0 || decimals > 77) decimals = 18;

        let divisor = 1n;
        for (let i = 0; i < decimals; i++) {
            divisor *= 10n;
        }

        const integerPart = bigVal / divisor;
        const fractionalPart = bigVal % divisor;

        const intStr = integerPart.toString();
        let fracStr = fractionalPart.toString();

        const paddingNeeded = decimals - fracStr.length;
        if (paddingNeeded > 0 && paddingNeeded < 100) {
            let padding = '';
            for (let i = 0; i < paddingNeeded; i++) {
                padding += '0';
            }
            fracStr = padding + fracStr;
        }

        if (fracStr.length > 18) {
            fracStr = fracStr.substring(0, 18);
        }

        fracStr = fracStr.replace(/0+$/, '');

        return fracStr ? `${intStr}.${fracStr}` : intStr;
    } catch (err) {
        console.error('safeFormatUnits error:', err, value);
        return '0';
    }
}

export const useStake = (contractAddress, decimals = 18) => {
    const config = useConfig();
    const { address: userAddress } = useAccount();

    const [stakes, setStakes] = useState({
        amount: { raw: '0', readable: '0' },
        restakeAmount: { raw: '0', readable: '0' },
        total: { raw: '0', readable: '0' },
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStakes = useCallback(async () => {
        if (!contractAddress || !userAddress) return;

        try {
            setLoading(true);
            setError(null);

            const availableStakes = await readContract(config, {
                address: contractAddress,
                abi: newPool.abi,
                functionName: 'stakes',
                args: [userAddress],
            });

            const amount = availableStakes?.[0] ?? availableStakes.amount;
            const restakeAmount = availableStakes?.[1] ?? availableStakes.restakeAmount;

            const amountRaw = amount?.toString() || '0';
            const restakeAmountRaw = restakeAmount?.toString() || '0';

            const amountReadable = safeFormatUnits(amount || 0, decimals);
            const restakeAmountReadable = safeFormatUnits(restakeAmount || 0, decimals);

            const totalRaw = (BigInt(amountRaw) + BigInt(restakeAmountRaw)).toString();
            const totalReadable = safeFormatUnits(BigInt(totalRaw), decimals);

            setStakes({
                amount: {
                    raw: amountRaw,
                    readable: amountReadable,
                },
                restakeAmount: {
                    raw: restakeAmountRaw,
                    readable: restakeAmountReadable,
                },
                total: {
                    raw: totalRaw,
                    readable: totalReadable,
                },
            });
        } catch (err) {
            console.error('Error fetching available stakes:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [config, contractAddress, userAddress, decimals]);

    useEffect(() => {
        fetchStakes();
    }, [fetchStakes]);

    return {
        stakes,
        loading,
        error,
        refetch: fetchStakes,
    };
};